import { Switch } from '@headlessui/react';
import React, { PropsWithChildren } from 'react';

type OwnProps = {
  enabled: boolean;
  setEnabled: (value: boolean) => void;
  labels?: {
    on: string;
    off: string;
  };
  smallLabels?: boolean;
  alwaysShowLabels?: boolean;
  greyDisabledLabels?: boolean;
  toggleDisabled?: boolean;
};
export default function Toggle({
  enabled,
  setEnabled,
  labels = { on: 'ON', off: 'OFF' },
  alwaysShowLabels = false,
  smallLabels = false,
  greyDisabledLabels,
  toggleDisabled,
}: PropsWithChildren<OwnProps>): JSX.Element {
  /*
   * If greyDisabledLabels is true, the inactive side's label is greyed out.
   * If alwaysShowLabels is false, the inactive side's label is hidden.
   * Otherwise, both labels are shown in black.
   */
  const getOnLabelColorClass = () => {
    if (!enabled && !alwaysShowLabels) {
      return 'invisible';
    }

    if (!enabled && greyDisabledLabels) {
      return 'text-textNeutral';
    }
    return '';
  };

  const getOffLabelColorClass = () => {
    if (enabled && !alwaysShowLabels) {
      return 'invisible';
    }

    if (enabled && greyDisabledLabels) {
      return 'text-textNeutral';
    }
    return '';
  };

  const labelSizeClass = smallLabels ? 'text-sm my-auto' : '';

  const backgroundColor = () => {
    if (toggleDisabled) {
      return 'bg-iconTertiary';
    }

    return enabled ? 'bg-primary' : 'bg-iconTertiary';
  };

  return (
    <Switch.Group>
      <Switch.Label passive className={`${labelSizeClass} ${getOffLabelColorClass()} mr-2`}>
        {labels.off}
      </Switch.Label>
      <Switch
        disabled={toggleDisabled}
        checked={enabled}
        data-testid="visibilityToggle"
        onChange={setEnabled}
        className={`${backgroundColor()} relative inline-flex w-14 items-center rounded-full p-1 transition-colors`}
      >
        <span
          data-testid="publicPrivateToggle"
          className={`${
            enabled ? 'translate-x-6' : 'translate-x-0'
          } inline-block h-6 w-6 transform rounded-full bg-textButton duration-100`}
        />
      </Switch>
      <Switch.Label passive className={`${labelSizeClass} ${getOnLabelColorClass()} ml-2`}>
        {labels.on}
      </Switch.Label>
    </Switch.Group>
  );
}
Toggle.defaultProps = {
  labels: {
    on: 'ON',
    off: 'OFF',
  },
  alwaysShowLabels: false,
  smallLabels: false,
  toggleDisabled: false,
  greyDisabledLabels: false,
};
